import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Slider,
  Stack,
  Typography,
} from '@mui/material';

interface MaskEditorProps {
  imageUrl: string;
  disabled?: boolean;
  onMaskChange: (dataUrl: string | null) => void;
}

type MaskTool = 'paint' | 'erase';

interface MaskHistoryEntry {
  mask: ImageData;
  overlay: ImageData;
}

const MASK_OVERLAY_COLOR = 'rgba(255, 64, 129, 0.55)';

const MaskEditor: React.FC<MaskEditorProps> = ({ imageUrl, disabled = false, onMaskChange }) => {
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const historyRef = useRef<MaskHistoryEntry[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [brushSize, setBrushSize] = useState(48);
  const [tool, setTool] = useState<MaskTool>('paint');
  const [hasMask, setHasMask] = useState(false);
  const [cursor, setCursor] = useState<{ x: number; y: number; size: number } | null>(null);

  const getContexts = () => {
    const maskCanvas = maskCanvasRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    const maskContext = maskCanvas?.getContext('2d');
    const overlayContext = overlayCanvas?.getContext('2d');
    if (!maskCanvas || !overlayCanvas || !maskContext || !overlayContext) return null;
    return { maskCanvas, overlayCanvas, maskContext, overlayContext };
  };

  const maskCanvasHasPixels = (canvas: HTMLCanvasElement, context: CanvasRenderingContext2D): boolean => {
    const data = context.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let index = 3; index < data.length; index += 4) {
      if (data[index] > 0) {
        return true;
      }
    }
    return false;
  };

  const exportMask = () => {
    const contexts = getContexts();
    if (!contexts) {
      onMaskChange(null);
      return;
    }

    const { maskCanvas, maskContext } = contexts;
    const nextHasMask = maskCanvasHasPixels(maskCanvas, maskContext);
    setHasMask(nextHasMask);
    if (!nextHasMask) {
      onMaskChange(null);
      return;
    }

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = maskCanvas.width;
    exportCanvas.height = maskCanvas.height;
    const exportContext = exportCanvas.getContext('2d');
    if (!exportContext) {
      onMaskChange(null);
      return;
    }

    // OpenAI-style masks: opaque pixels are preserved, transparent pixels are edited.
    exportContext.fillStyle = 'rgba(255,255,255,1)';
    exportContext.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportContext.globalCompositeOperation = 'destination-out';
    exportContext.drawImage(maskCanvas, 0, 0);
    exportContext.globalCompositeOperation = 'source-over';
    onMaskChange(exportCanvas.toDataURL('image/png'));
  };

  const pushHistory = () => {
    const contexts = getContexts();
    if (!contexts) return;
    const { maskCanvas, overlayCanvas, maskContext, overlayContext } = contexts;
    const nextHistory = [
      ...historyRef.current.slice(-19),
      {
        mask: maskContext.getImageData(0, 0, maskCanvas.width, maskCanvas.height),
        overlay: overlayContext.getImageData(0, 0, overlayCanvas.width, overlayCanvas.height),
      },
    ];
    historyRef.current = nextHistory;
    setCanUndo(nextHistory.length > 0);
  };

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / Math.max(1, rect.width);
    const scaleY = canvas.height / Math.max(1, rect.height);
    const cssX = event.clientX - rect.left;
    const cssY = event.clientY - rect.top;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
      cssX,
      cssY,
      cssSize: brushSize / Math.max(scaleX, 0.001),
    };
  };

  const drawSegment = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const contexts = getContexts();
    if (!contexts) return;
    const { maskContext, overlayContext } = contexts;

    for (const context of [maskContext, overlayContext]) {
      context.save();
      context.globalCompositeOperation = tool === 'erase' ? 'destination-out' : 'source-over';
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.lineWidth = brushSize;
      context.strokeStyle = context === maskContext ? 'rgba(0,0,0,1)' : MASK_OVERLAY_COLOR;
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.lineTo(to.x, to.y);
      context.stroke();
      context.restore();
    }

    if (tool === 'paint') {
      setHasMask(true);
    }
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || drawingRef.current) return;
    const point = pointFromEvent(event);
    if (!point) return;
    drawingRef.current = true;
    setCursor({ x: point.cssX, y: point.cssY, size: point.cssSize });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pushHistory();
    lastPointRef.current = point;
    drawSegment(point, point);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const point = pointFromEvent(event);
    if (point) {
      setCursor({ x: point.cssX, y: point.cssY, size: point.cssSize });
    }
    if (!drawingRef.current) return;
    const previous = lastPointRef.current;
    if (!point || !previous) return;
    drawSegment(previous, point);
    lastPointRef.current = point;
  };

  const finishDrawing = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    exportMask();
  };

  const handlePointerLeave = () => {
    setCursor(null);
    finishDrawing();
  };

  const handleClear = () => {
    const contexts = getContexts();
    if (!contexts) return;
    pushHistory();
    contexts.maskContext.clearRect(0, 0, contexts.maskCanvas.width, contexts.maskCanvas.height);
    contexts.overlayContext.clearRect(0, 0, contexts.overlayCanvas.width, contexts.overlayCanvas.height);
    setHasMask(false);
    onMaskChange(null);
  };

  const handleUndo = () => {
    const contexts = getContexts();
    const previous = historyRef.current.pop();
    setCanUndo(historyRef.current.length > 0);
    if (!contexts || !previous) return;
    contexts.maskContext.putImageData(previous.mask, 0, 0);
    contexts.overlayContext.putImageData(previous.overlay, 0, 0);
    setTimeout(exportMask, 0);
  };

  const handleExpandMask = () => {
    const contexts = getContexts();
    if (!contexts || !hasMask) return;
    pushHistory();
    const { maskCanvas, overlayCanvas, maskContext, overlayContext } = contexts;
    const maskSnapshot = document.createElement('canvas');
    maskSnapshot.width = maskCanvas.width;
    maskSnapshot.height = maskCanvas.height;
    maskSnapshot.getContext('2d')?.drawImage(maskCanvas, 0, 0);
    const overlaySnapshot = document.createElement('canvas');
    overlaySnapshot.width = overlayCanvas.width;
    overlaySnapshot.height = overlayCanvas.height;
    overlaySnapshot.getContext('2d')?.drawImage(overlayCanvas, 0, 0);

    const radius = Math.max(2, Math.round(brushSize * 0.12));
    for (let y = -radius; y <= radius; y += radius) {
      for (let x = -radius; x <= radius; x += radius) {
        if (x === 0 && y === 0) continue;
        maskContext.drawImage(maskSnapshot, x, y);
        overlayContext.drawImage(overlaySnapshot, x, y);
      }
    }
    exportMask();
  };

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const width = Math.max(1, img.naturalWidth || 1);
      const height = Math.max(1, img.naturalHeight || 1);
      setNaturalSize({ width, height });
      for (const canvas of [maskCanvasRef.current, overlayCanvasRef.current]) {
        if (!canvas) continue;
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d')?.clearRect(0, 0, width, height);
      }
      historyRef.current = [];
      setCanUndo(false);
      setHasMask(false);
      onMaskChange(null);
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl, onMaskChange]);

  return (
    <Box>
      <Box
        sx={{
          width: '100%',
          textAlign: 'center',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#111',
          border: '1px solid',
          borderColor: 'divider',
          userSelect: 'none',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            maxWidth: '100%',
            verticalAlign: 'top',
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt="Basbild för redigering"
            draggable={false}
            sx={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: { xs: 420, xl: 620 },
              width: 'auto',
              height: 'auto',
            }}
          />
          <Box
            component="canvas"
            ref={maskCanvasRef}
            sx={{ display: 'none' }}
          />
          <Box
            component="canvas"
            ref={overlayCanvasRef}
            aria-label="Maskyta"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishDrawing}
            onPointerCancel={finishDrawing}
            onPointerLeave={handlePointerLeave}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              cursor: disabled ? 'not-allowed' : 'none',
              touchAction: 'none',
            }}
          />
          {cursor && !disabled && (
            <Box
              sx={{
                position: 'absolute',
                left: cursor.x,
                top: cursor.y,
                width: cursor.size,
                height: cursor.size,
                border: '2px solid rgba(255,255,255,0.95)',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.55)',
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            />
          )}
        </Box>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1.5 }}>
        <ButtonGroup size="small" variant="outlined" disabled={disabled}>
          <Button variant={tool === 'paint' ? 'contained' : 'outlined'} onClick={() => setTool('paint')}>
            Måla
          </Button>
          <Button variant={tool === 'erase' ? 'contained' : 'outlined'} onClick={() => setTool('erase')}>
            Sudda
          </Button>
        </ButtonGroup>
        <Box sx={{ minWidth: 180, flex: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Pensel: {brushSize}px
          </Typography>
          <Slider
            size="small"
            min={8}
            max={160}
            value={brushSize}
            disabled={disabled}
            onChange={(_, value) => setBrushSize(Array.isArray(value) ? value[0] : value)}
          />
        </Box>
        <Button size="small" variant="outlined" onClick={handleUndo} disabled={disabled || !canUndo}>
          Ångra
        </Button>
        <Button size="small" variant="outlined" onClick={handleExpandMask} disabled={disabled || !hasMask}>
          Expandera mask
        </Button>
        <Button size="small" color="inherit" onClick={handleClear} disabled={disabled || !naturalSize}>
          Rensa
        </Button>
      </Stack>
    </Box>
  );
};

export default MaskEditor;
